import { CTAButton } from '@gamma/ui/buttons';
import { Input } from '@gamma/ui/form-fields';
import { ErrorBoundary } from '@gamma/ui/overlay';
import {
  CardPageBody,
  CardPageCTA,
  CardPageHeading,
  CardPageIllustration,
  CardPageSubtitle,
} from '@gamma/ui/pages';
import { formatOTPCode } from '@gamma/ui/util';
import { ROUTES } from '@investigator/shared/constants';
import {
  useHandleNextURLRedirect,
  useMfaSessionValidation,
  useTokenManagement,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { setRole } from '@investigator/shared/utils';
import { Auth } from 'aws-amplify';
import { ChangeEvent, useContext, useState, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AuthState } from '../../../../shared/types/auth-types';
import { getMfaErrorType } from '@investigator/shared/utils/getMfaErrorType';

const { base } = ROUTES;

export const Confirm2fa = () => {
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    setFocus,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<{ OTPCode: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user, setAuthState, setUser, setUserRole } = useContext(AuthContext);
  const navigate = useNavigate();

  const { saveIdToken } = useTokenManagement();

  // Validate MFA user session
  useMfaSessionValidation(user);

  const handleOTPInputChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    if (errors.OTPCode) {
      clearErrors('OTPCode');
    }

    setValue('OTPCode', formatOTPCode(value));
  };

  const handleRedirectURL = useHandleNextURLRedirect(base);

  const handleSubmission: SubmitHandler<{ OTPCode: string }> = async ({
    OTPCode,
  }) => {
    setIsLoading(true);
    try {
      // Ensure user session is still valid before attempting MFA confirmation
      if (!user) {
        navigate('/login?error=mfa_session_expired');
        return;
      }

      const verifiedUser = await Auth.confirmSignIn(
        user,
        OTPCode.replace(' ', ''),
        'SOFTWARE_TOKEN_MFA',
      );

      if (verifiedUser) {
        const currentUser = await Auth.currentAuthenticatedUser();
        if (currentUser) {
          setUser?.(currentUser);
          setRole({ user: currentUser, setUserRole });

          const idToken = currentUser?.signInUserSession?.idToken?.jwtToken;
          if (idToken) {
            saveIdToken(idToken, currentUser);
          }

          await handleRedirectURL();

          // Clean up navigation state
          if (localStorage.getItem('next')) {
            localStorage.removeItem('next');
          }
        }
        setAuthState?.(AuthState.SignedIn);
      }
    } catch (err) {
      const { errorType } = getMfaErrorType(err);
      switch (errorType) {
        case 'session':
          navigate('/login?error=mfa_session_expired');
          break;
        case 'otp':
          setError('OTPCode', { message: i18n.forms.validation.otp });
          break;
        case 'generic':
          localStorage.removeItem('docs_id_token');
          setError('OTPCode', { message: i18n.forms.validation.otp });
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setFocus('OTPCode');
  }, [setFocus]);

  return (
    <ErrorBoundary page="Registration MFA Confirm OTP Page">
      <CardPageHeading>{i18n.forms.labels.MFA.title}</CardPageHeading>
      <CardPageIllustration illustration="LockedShield" />
      <CardPageSubtitle>{i18n.forms.labels.MFA.instructions}</CardPageSubtitle>
      <CardPageBody>
        <form onSubmit={handleSubmit(handleSubmission)} id="confirmOTPCode">
          <Input
            label={i18n.forms.labels.enterOTP}
            autoComplete="one-time-code"
            placeholder="xxx xxx"
            data-testid="mfa-input"
            {...register('OTPCode', {
              onChange: handleOTPInputChange,
            })}
            error={errors?.OTPCode?.message}
          />
        </form>
      </CardPageBody>
      <CardPageCTA>
        <CTAButton
          type="submit"
          form="confirmOTPCode"
          isDisabled={!isDirty}
          isLoading={isLoading}
          data-testid="submit-mfa-button"
        >
          Submit
        </CTAButton>
      </CardPageCTA>
    </ErrorBoundary>
  );
};
